import { useEffect, useState, useMemo, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  FitnessCenter as FitnessCenterIcon,
  SportsFootball as SportsFootballIcon,
  PersonOutline as PersonOutlineIcon,
  Groups as GroupsIcon,
} from "@mui/icons-material";

import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

import apiFetch from "services/http";
import { useSeason } from "context/SeasonContext";
import { PageToolbar, StatCard, useNotify } from "components/Convokko";
import { getProximoPartidoPendiente, tieneConvocatoria } from "utils/convocatoria";

const truncateLabel = (label = "", maxLength = 8) =>
  label.length <= maxLength ? label : `${label.substring(0, maxLength)}...`;

const emptyChart = { labels: [], datasets: { label: "", data: [] } };

export default function Dashboard() {
  const { temporada } = useSeason();
  const navigate = useNavigate();
  const notify = useNotify();

  const [rol, setRol] = useState("");
  const [equipos, setEquipos] = useState([]);
  const [equipoSeleccionado, setEquipoSeleccionado] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [kpis, setKpis] = useState({
    jugadores: 0,
    entrenadores: 0,
    entrenamientos: 0,
    partidos: 0,
  });

  const [asistenciaDiasData, setAsistenciaDiasData] = useState(emptyChart);
  const [asistenciaJugadorData, setAsistenciaJugadorData] = useState(emptyChart);
  const [asistenciaPartidosData, setAsistenciaPartidosData] = useState(emptyChart);
  const [entrenamientosPorEquipoData, setEntrenamientosPorEquipoData] = useState(emptyChart);
  const [convocatoriasPorEquipoData, setConvocatoriasPorEquipoData] = useState(emptyChart);
  const [partidosPendientesPorEquipoData, setPartidosPendientesPorEquipoData] =
    useState(emptyChart);
  const [equiposResumen, setEquiposResumen] = useState([]);
  const [partidosAviso, setPartidosAviso] = useState([]);

  const equiposTemporada = useMemo(
    () => equipos.filter((e) => Number(e.temporada) === Number(temporada)),
    [equipos, temporada]
  );

  const esVistaGeneral = rol === "coordinador" && !equipoSeleccionado;

  useEffect(() => {
    const init = async () => {
      try {
        const token = localStorage.getItem("token");
        const decoded = jwtDecode(token);
        const rolUsuario = decoded.rol || "entrenador";
        setRol(rolUsuario);

        const [dataEquipos, dataPartidos] = await Promise.all([
          apiFetch("/api/equipos"),
          apiFetch("/api/partidos/ver-partidos-equipo"),
        ]);
        const lista = Array.isArray(dataEquipos) ? dataEquipos : [dataEquipos];
        setEquipos(lista);
        setPartidosAviso(Array.isArray(dataPartidos) ? dataPartidos : []);
        if (rolUsuario !== "coordinador" && lista[0]?._id) {
          setEquipoSeleccionado(lista[0]._id);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setInitialized(true);
      }
    };
    init();
  }, []);

  // Si el equipo seleccionado deja de pertenecer a la temporada activa, se resetea
  useEffect(() => {
    if (
      rol === "coordinador" &&
      equipoSeleccionado &&
      !equiposTemporada.some((e) => e._id === equipoSeleccionado)
    ) {
      setEquipoSeleccionado("");
    }
  }, [temporada, rol, equipoSeleccionado, equiposTemporada]);

  const fetchDashboard = useCallback(async () => {
    if (!rol || !initialized) return;
    setLoading(true);
    setError("");
    try {
      if (rol === "coordinador" && !equipoSeleccionado) {
        const [resumen, entrenamientosEquiposData, partidosEquiposData] = await Promise.all([
          apiFetch("/api/dashboard/resumen"),
          apiFetch("/api/dashboard/entrenamientos"),
          apiFetch("/api/dashboard/partidos"),
        ]);

        setKpis({
          jugadores: resumen.cantidad_jugadores || 0,
          entrenadores: resumen.cantidad_entrenadores || 0,
          entrenamientos: resumen.cantidad_entrenamientos || 0,
          partidos: resumen.cantidad_partidos || 0,
        });

        const entrenamientosEquipos = entrenamientosEquiposData.entrenamientos || [];
        setEntrenamientosPorEquipoData({
          labels: entrenamientosEquipos.map((e) => truncateLabel(e.nombre_equipo)),
          datasets: {
            label: "Entrenamientos",
            data: entrenamientosEquipos.map((e) => e.total_entrenamientos),
          },
        });

        const partidosEquipos = partidosEquiposData.partidos_equipos || [];
        const porConvocatorias = [...partidosEquipos].sort(
          (a, b) => b.total_convocatorias - a.total_convocatorias
        );
        setConvocatoriasPorEquipoData({
          labels: porConvocatorias.map((e) => truncateLabel(e.nombre_equipo)),
          datasets: {
            label: "Convocatorias",
            data: porConvocatorias.map((e) => e.total_convocatorias),
          },
        });

        const porPendientes = [...partidosEquipos].sort(
          (a, b) => b.partidos_pendientes_pasados - a.partidos_pendientes_pasados
        );
        setPartidosPendientesPorEquipoData({
          labels: porPendientes.map((e) => truncateLabel(e.nombre_equipo)),
          datasets: {
            label: "Pendientes",
            data: porPendientes.map((e) => e.partidos_pendientes_pasados),
          },
        });

        const resumenTabla = partidosEquipos.map((equipo) => ({
          nombre: equipo.nombre_equipo,
          entrenamientos:
            entrenamientosEquipos.find((e) => e._id === equipo._id)?.total_entrenamientos || 0,
          partidos_finalizados: equipo.partidos_finalizados,
          con_convocatoria: equipo.total_convocatorias,
          pendientes_atrasados: equipo.partidos_pendientes_pasados,
          pendientes_futuros: equipo.partidos_pendientes_futuros,
        }));
        resumenTabla.sort((a, b) => a.nombre.localeCompare(b.nombre));
        setEquiposResumen(resumenTabla);
      } else {
        const params =
          rol === "coordinador" && equipoSeleccionado
            ? { equipo_id: equipoSeleccionado }
            : undefined;

        const [resumen, diasData, asistenciaData, asistPartidosData] = await Promise.all([
          apiFetch("/api/dashboard/resumen", { params }),
          apiFetch("/api/dashboard/mis-entrenamientos/por-fecha", { params }),
          apiFetch("/api/dashboard/mis-entrenamientos/summary", { params }),
          apiFetch("/api/dashboard/mis-partidos/summary", { params }),
        ]);

        setKpis({
          jugadores: resumen.cantidad_jugadores || 0,
          entrenadores: resumen.cantidad_entrenadores || 0,
          entrenamientos: resumen.cantidad_entrenamientos || 0,
          partidos: resumen.cantidad_partidos || 0,
        });

        setAsistenciaDiasData({
          labels: (diasData.entrenamientos || []).map((e) => `${e.dia}`),
          datasets: {
            label: "Asistencia",
            data: (diasData.entrenamientos || []).map((e) => e.asistencias_totales),
          },
        });
        setAsistenciaJugadorData({
          labels: (asistenciaData.jugadores || []).map((j) => truncateLabel(j.nombre)),
          datasets: {
            label: "Asistencia",
            data: (asistenciaData.jugadores || []).map((j) => j.asistencias_totales),
          },
        });
        setAsistenciaPartidosData({
          labels: (asistPartidosData.jugadores || []).map((j) => truncateLabel(j.nombre)),
          datasets: {
            label: "Asistencia",
            data: (asistPartidosData.jugadores || []).map((j) => j.asistencias_totales),
          },
        });
      }
    } catch (err) {
      setError(err.message || "Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  }, [rol, initialized, equipoSeleccionado]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Aviso de "próximo partido": recuerda hacer la convocatoria si falta poco
  // y todavía no se ha hecho, o simplemente recuerda que el partido se acerca.
  // Se restringe a los equipos de la temporada activa (igual que el listado de
  // Partidos), si no un partido de una temporada distinta a la seleccionada
  // podía colarse aquí aunque no apareciera en el listado.
  const partidosRelevantes = useMemo(() => {
    const idsTemporada = new Set(equiposTemporada.map((e) => e._id));
    const enTemporada = partidosAviso.filter((p) => idsTemporada.has(p.id_equipo));
    if (rol === "coordinador" && equipoSeleccionado) {
      return enTemporada.filter((p) => p.id_equipo === equipoSeleccionado);
    }
    return enTemporada;
  }, [partidosAviso, equiposTemporada, rol, equipoSeleccionado]);

  const avisoPartido = useMemo(() => {
    const proximo = getProximoPartidoPendiente(partidosRelevantes);
    if (!proximo || proximo.dias > 3) return null;
    const equipo = equipos.find((e) => e._id === proximo.partido.id_equipo);
    return {
      partido: proximo.partido,
      dias: proximo.dias,
      equipoNombre: equipo?.nombre || "",
      convocatoriaHecha: tieneConvocatoria(proximo.partido),
    };
  }, [partidosRelevantes, equipos]);

  const renderSkeleton = () => (
    <Grid container spacing={3}>
      {[...Array(4)].map((_, i) => (
        <Grid item xs={12} md={6} lg={3} key={i}>
          <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
        </Grid>
      ))}
      {[...Array(3)].map((_, i) => (
        <Grid item xs={12} md={6} lg={4} key={`c${i}`}>
          <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3 }} />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <PageToolbar
          icon={DashboardIcon}
          title="Resumen"
          count={equiposTemporada.length}
          countLabel={equiposTemporada.length === 1 ? "equipo" : "equipos"}
          filters={
            rol === "coordinador"
              ? [
                  {
                    id: "equipo",
                    label: "Equipo",
                    allLabel: "Vista general",
                    value: equipoSeleccionado,
                    onChange: setEquipoSeleccionado,
                    options: equiposTemporada.map((e) => ({ value: e._id, label: e.nombre })),
                    minWidth: 150,
                  },
                ]
              : []
          }
        />

        {avisoPartido && (
          <Alert
            severity={avisoPartido.convocatoriaHecha ? "info" : "warning"}
            sx={{
              mb: 3,
              borderRadius: 3,
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              "& .MuiAlert-message": { width: "100%" },
              "& .MuiAlert-action": {
                width: { xs: "100%", sm: "auto" },
                alignSelf: "stretch",
                ml: { xs: 0, sm: 1 },
                mt: { xs: 1, sm: 0 },
                pt: 0,
              },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() =>
                  navigate("/partidos", {
                    state: { openConvocatoriaPartidoId: avisoPartido.partido._id },
                  })
                }
                sx={{
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  width: { xs: "100%", sm: "auto" },
                  border: { xs: "1px solid", sm: "none" },
                  borderColor: "currentColor",
                }}
              >
                {avisoPartido.convocatoriaHecha ? "Ver convocatoria" : "Hacer convocatoria"}
              </Button>
            }
          >
            {`⚽ Partido ${
              avisoPartido.dias === 0
                ? "hoy"
                : avisoPartido.dias === 1
                ? "mañana"
                : `en ${avisoPartido.dias} días`
            } · ${avisoPartido.equipoNombre ? `${avisoPartido.equipoNombre} ` : ""}vs ${
              avisoPartido.partido.equipo_rival || "rival"
            }${avisoPartido.partido.hora ? ` a las ${avisoPartido.partido.hora}` : ""}. ${
              avisoPartido.convocatoriaHecha
                ? "Ya tienes la convocatoria hecha."
                : "Todavía no has hecho la convocatoria."
            }`}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          renderSkeleton()
        ) : esVistaGeneral ? (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  tone="navy"
                  icon={GroupsIcon}
                  label="Equipos"
                  value={equiposTemporada.length}
                  onClick={() => navigate("/equipos")}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  tone="primary"
                  icon={PeopleIcon}
                  label="Jugadores"
                  value={kpis.jugadores}
                  onClick={() => navigate("/jugadores")}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  tone="success"
                  icon={PersonOutlineIcon}
                  label="Entrenadores"
                  value={kpis.entrenadores}
                  onClick={() => navigate("/entrenadores")}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  tone="info"
                  icon={FitnessCenterIcon}
                  label="Entrenamientos"
                  value={kpis.entrenamientos}
                  onClick={() => navigate("/entrenamientos")}
                />
              </Grid>
            </Grid>

            <MDBox mt={4}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={4}>
                  <ReportsBarChart
                    color="dark"
                    title="Entrenamientos por equipo"
                    description="Total de entrenamientos registrados"
                    date={`Equipos activos: ${equiposTemporada.length}`}
                    chart={entrenamientosPorEquipoData}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <ReportsBarChart
                    color="dark"
                    title="Convocatorias por equipo"
                    description="Partidos con convocatoria registrada"
                    date={`Equipos activos: ${equiposTemporada.length}`}
                    chart={convocatoriasPorEquipoData}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <ReportsBarChart
                    color="dark"
                    title="Partidos pendientes"
                    description="Partidos atrasados sin resultado"
                    date={`Equipos activos: ${equiposTemporada.length}`}
                    chart={partidosPendientesPorEquipoData}
                  />
                </Grid>
              </Grid>
            </MDBox>

            <MDBox mt={4}>
              <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Box p={3} pb={1}>
                  <Typography variant="h6" fontWeight={700}>
                    Resumen detallado por equipo
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Estado de cada equipo activo de la escuela
                  </Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 480 }}>
                  <Table stickyHeader size="small">
                    <TableHead sx={{ display: "table-header-group" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem" }}>EQUIPO</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>
                          ENTRENAMIENTOS
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>
                          P. FINALIZADOS
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>
                          CON CONVOCATORIA
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>
                          PEND. ATRASADOS
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.72rem" }}>
                          PEND. FUTUROS
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {equiposResumen.map((equipo) => (
                        <TableRow key={equipo.nombre} hover>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            {equipo.nombre}
                          </TableCell>
                          <TableCell align="center">{equipo.entrenamientos}</TableCell>
                          <TableCell align="center">{equipo.partidos_finalizados}</TableCell>
                          <TableCell align="center">{equipo.con_convocatoria}</TableCell>
                          <TableCell align="center">
                            {equipo.pendientes_atrasados > 0 ? (
                              <Chip
                                label={equipo.pendientes_atrasados}
                                color="error"
                                size="small"
                                sx={{ fontWeight: 700, minWidth: 34 }}
                              />
                            ) : (
                              0
                            )}
                          </TableCell>
                          <TableCell align="center">{equipo.pendientes_futuros}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </MDBox>
          </>
        ) : (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  tone="primary"
                  icon={PeopleIcon}
                  label="Jugadores"
                  value={kpis.jugadores}
                  onClick={() => navigate("/jugadores")}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  tone="success"
                  icon={PersonOutlineIcon}
                  label="Entrenadores"
                  value={kpis.entrenadores}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  tone="info"
                  icon={FitnessCenterIcon}
                  label="Entrenamientos"
                  value={kpis.entrenamientos}
                  onClick={() => navigate("/entrenamientos")}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  tone="navy"
                  icon={SportsFootballIcon}
                  label="Partidos"
                  value={kpis.partidos}
                  onClick={() => navigate("/partidos")}
                />
              </Grid>
            </Grid>

            <MDBox mt={4}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={4}>
                  <ReportsBarChart
                    color="dark"
                    title="Asistencia a entrenamientos"
                    description="Últimos 10 entrenamientos"
                    date={`Total entrenamientos: ${kpis.entrenamientos}`}
                    chart={asistenciaDiasData}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <ReportsBarChart
                    color="dark"
                    title="Asistencia por jugador"
                    description="Entrenamientos a los que ha asistido cada jugador"
                    date={`Total entrenamientos: ${kpis.entrenamientos}`}
                    chart={asistenciaJugadorData}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <ReportsBarChart
                    color="dark"
                    title="Convocatorias por jugador"
                    description="Partidos en los que ha sido convocado cada jugador"
                    date={`Total partidos: ${kpis.partidos}`}
                    chart={asistenciaPartidosData}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </>
        )}
      </Box>
      <Footer />
    </DashboardLayout>
  );
}
